import { useQuery } from '@apollo/client';
import { Alert, Box, Stack } from '@mui/material';
import { GridRowId, GridRowSelectionModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { DEFAULT_CURRENCY } from '@procureai/schemas';
import { CoverageAssistantFiltersPayload, CoverageAssistantFiltersPayloadErrorCode } from '@procureai/ui-modules-mui';
import { CurrencyProvider, useLanguageCode } from '@procureai/ui-mui';
import { isEmpty, isNil } from 'ramda';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import isEqual from 'react-fast-compare';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { COVERAGE_ASSISTANT_QUERY } from '../../CoverageAssistantWidget.query.gql';
import { CommodityUndercoverages } from '../../CoverageAssistantWidget.schema';
import { CoverageAssistantGridField, CoveragePredictionRow } from '../../CoverageAssistantWidget.types';
import { Language } from '@/generated/gql/graphql';
import { BaseDetailDomainType, I18nNamespace } from '~/common/types';
import {
  ColumnSelector,
  DataGridCustom,
  DataGridWidgetHeader,
  ResetRemoteStateButton,
  useDataGridRemoteState,
} from '~/components/DataGridCustom';
import { FormChangeDebouncer } from '~/components/FormChangeDebouncer/FormChangeDebouncer';
import { HoverMenuProvider } from '~/components/HoverMenu';
import { WidgetContentEmpty } from '~/components/WidgetContentEmpty/WidgetContentEmpty';

import { useCoverageAssistantGridConfig } from './CoverageAssistantPredictions.dataGrid';

const MAX_ITEMS = 50;

interface CoverageAssistantPredictionsGridProps {
  normalizedFilters: {
    payload: CoverageAssistantFiltersPayload;
    errorCode: CoverageAssistantFiltersPayloadErrorCode | null;
    canQuery: boolean;
  } | null;
  onChange?: (data: CommodityUndercoverages) => void;
}

const domain = BaseDetailDomainType.COVERAGE_ASSISTANT;

export const CoverageAssistantPredictionsGrid = ({
  normalizedFilters,
  onChange,
}: CoverageAssistantPredictionsGridProps) => {
  const { control, setValue } = useFormContext<CommodityUndercoverages>();
  const predictions = useWatch({ control, name: 'predictions' });
  const { languageCode } = useLanguageCode();

  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'CoverageAssistantWidget' });
  const apiRef = useGridApiRef();

  const payload = normalizedFilters?.payload;
  const dateFrom = payload?.timeframe?.[0];
  const dateTo = payload?.timeframe?.[1];
  const commodityGroupIds = payload?.commodityGroups ?? [];
  const organizationIds = payload?.organizations;
  const commodityIds = payload?.commodities;

  const handleRowSelectionChange = (newSelection: GridRowSelectionModel) => {
    // Update the form state when selection changes
    predictions.forEach((prediction, index) => {
      const isSelected = newSelection.includes(prediction.rowId);
      setValue(`predictions.${index}.selected`, isSelected, {
        shouldValidate: true,
        shouldDirty: true,
      });
    });
  };

  const getRowIndex = useCallback(
    (rowId: GridRowId) => {
      const rowIds = apiRef.current.getAllRowIds();
      return rowIds?.findIndex((rId) => rId === rowId);
    },
    [apiRef],
  );
  const gridConfig = useCoverageAssistantGridConfig({ getRowIndex });

  const {
    loading: loadingRemoteState,
    gridProps,
    onRemoteStateReset,
  } = useDataGridRemoteState({
    apiRef,
    config: gridConfig,
    domain,
  });

  const { fields, replace } = useFieldArray<CommodityUndercoverages>({
    name: 'predictions',
    shouldUnregister: true,
  });

  const skipQuery = !normalizedFilters?.canQuery || isNil(dateFrom) || isNil(dateTo);
  const { data, loading, error } = useQuery(COVERAGE_ASSISTANT_QUERY, {
    variables: {
      commodityGroupIds,
      organizationIds: organizationIds,
      commodityIds: commodityIds,
      languageCode: languageCode.toUpperCase() as Language,
      timeframeFrom: dateFrom?.toISOString().split('Z')[0],
      timeframeTo: dateTo?.toISOString().split('Z')[0],
    },
    skip: skipQuery,
  });

  const hasMoreThanMax = (data?.commodityUndercoverages.totalCount ?? 0) > MAX_ITEMS;
  const displayedPredictions = data?.commodityUndercoverages.undercoverages.slice(0, MAX_ITEMS);

  const transformedRows = useMemo<CoveragePredictionRow[]>(() => {
    return (
      displayedPredictions
        ?.map((prediction, index) => ({
          rowId: `${prediction.organizationId}-${prediction.commodityId}-${index}`,
          commodityId: prediction.commodityId,
          undercoveredQuantity: Number(prediction.undercoveredQuantity),
          unitId: prediction.unitId,
          priceUnit: Number(prediction.priceUnit),
          validFrom: prediction.validFrom,
          validTo: prediction.validTo,
          currency: prediction.currency,
          minimumOrderQuantity: Number(prediction.minimumOrderQuantity),
          qualifiedSupplierIds: prediction.qualifiedSupplierIds,
          organizationId: prediction.organizationId,
          commodityGroupId: prediction.commodityGroupId,
          title: prediction.title ?? '',
          description: prediction.description ?? '',
          selected: false,
        }))
        .sort((a, b) => b.undercoveredQuantity - a.undercoveredQuantity) ?? []
    );
  }, [displayedPredictions]);

  const transformedRowsRef = useRef(transformedRows);

  useEffect(() => {
    if (!isEqual(transformedRowsRef.current, transformedRows)) {
      transformedRowsRef.current = transformedRows;
      replace(transformedRows as any);
    }
  }, [replace, transformedRows]);

  const showEmptyState = isEmpty(commodityGroupIds);

  if (normalizedFilters?.errorCode) {
    const title = t(`filterErrors.${normalizedFilters.errorCode}`, { defaultValue: t('invalidFilterError') });
    return <WidgetContentEmpty title={title} color="error.main" />;
  }

  if (error) {
    return <WidgetContentEmpty title={t('loadingError')} color="error.main" />;
  }

  return (
    <>
      {onChange && <FormChangeDebouncer control={control} onChange={onChange} />}
      {showEmptyState ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
            backgroundColor: 'background.paper',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <WidgetContentEmpty title={t('emptyState')} />
        </Box>
      ) : (
        <Stack spacing={2}>
          <CurrencyProvider value={DEFAULT_CURRENCY}>
            <Box height={600}>
              <DataGridWidgetHeader
                disablePadding
                right={
                  <Stack direction="row">
                    <ColumnSelector
                      columns={gridProps.columns}
                      columnVisibilityModel={gridProps.columnVisibilityModel}
                      onColumnVisibilityModelChange={gridProps.onColumnVisibilityModelChange}
                    />
                    <ResetRemoteStateButton onRemoteStateReset={onRemoteStateReset} />
                  </Stack>
                }
              />
              <HoverMenuProvider>
                <DataGridCustom
                  rows={fields}
                  loading={loading || loadingRemoteState}
                  getRowId={(row) => row.rowId}
                  onRowSelectionModelChange={handleRowSelectionChange}
                  disableRowSelectionOnClick
                  hideFooter
                  checkboxSelection
                  density="compact"
                  apiRef={apiRef}
                  rowSelection
                  sx={{
                    '& .MuiDataGrid-row': { cursor: 'default' },
                  }}
                  {...gridProps}
                  initialState={{
                    pinnedColumns: {
                      right: [CoverageAssistantGridField.BASELINE_PRICE_PER_PRICE_UNIT],
                    },
                  }}
                />
              </HoverMenuProvider>
            </Box>
          </CurrencyProvider>
          {hasMoreThanMax && (
            <Alert severity="warning" icon={false}>
              {t('maxItemsExceeded')}
            </Alert>
          )}
        </Stack>
      )}
    </>
  );
};
